/**
 * app-layout.tsx — Top-level chrome for every authenticated route.
 *
 * Adopts the التزام design system imported from the Crextio-style
 * wireframe in [docs/wireframes/iltizam2/app.jsx](../../docs/wireframes/iltizam2/app.jsx):
 *
 *   - Cream→peach diagonal gradient background (driven by body
 *     styles in src/index.css, NOT by a wrapper className, so
 *     anchor scrolling never reveals slate underneath).
 *   - Top-of-page pill navigation via `AppTopNav`. The previous
 *     left-side `AppSidebar` is retired.
 *   - Generous 1280px max-width content column with 26/30px
 *     horizontal padding (matching the wireframe's outer chrome).
 *
 * Everything inside `<Outlet />` is still per-page React; existing
 * pages render against the new design tokens until they're each
 * migrated to the bento aesthetic in subsequent phases.
 */

import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { AppTopNav } from '@/components/app-top-nav';
import { DemoRoleSwitcher } from '@/components/demo-role-switcher';
import { ReadOnlyBanner } from '@/components/read-only-banner';
import { LoadingState } from '@/components/loading-state';

export function AppLayout(): JSX.Element {
  return (
    <div className="min-h-dvh">
      <ReadOnlyBanner />
      <div className="mx-auto max-w-[1280px] px-[26px] pb-[60px] pt-[26px] sm:px-[30px]">
        <AppTopNav />
        <main>
          {/* Suspense boundary for lazily code-split route pages. The
              chrome (nav + banner) stays painted while the page chunk
              loads, so navigation shows a spinner in-place rather than
              a blank screen. */}
          <Suspense fallback={<LoadingState />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <DemoRoleSwitcher />
    </div>
  );
}
